/**
 * MoveShapeHandler that guards against null parent references.
 * The default handler crashes when shape.parent or newParent is null (e.g. pool drop from palette).
 */
import inherits from 'inherits-browser';
import MoveShapeHandler from 'diagram-js/lib/features/modeling/cmd/MoveShapeHandler';
import { assign, pick } from 'min-dash';
import { add as collectionAdd, remove as collectionRemove } from 'diagram-js/lib/util/Collections';

function SafeMoveShapeHandler(this: any, modeling: any, canvas: any) {
  MoveShapeHandler.call(this, modeling);
  this._canvas = canvas;
}

(SafeMoveShapeHandler as any).$inject = ['modeling', 'canvas'];
inherits(SafeMoveShapeHandler, MoveShapeHandler);

SafeMoveShapeHandler.prototype.execute = function (context: any) {
  const shape = context.shape;
  const delta = context.delta;
  const root = this._canvas.getRootElement();

  let newParent = context.newParent || shape.parent;
  const newParentIndex = context.newParentIndex;
  let oldParent = shape.parent;

  // Guard: ensure both parents are valid (e.g. pool dropped from palette)
  if (!newParent && root) newParent = root;
  if (!oldParent && root) {
    oldParent = root;
    shape.parent = root;
    if (!root.children) root.children = [];
    root.children.push(shape);
  }
  if (!oldParent) oldParent = newParent;
  if (!newParent) newParent = oldParent;
  if (!oldParent || !newParent) {
    context.oldParent = shape.parent || root;
    context.oldParentIndex = (shape.parent?.children?.indexOf(shape)) ?? -1;
    return shape; // cannot safely move
  }

  context.oldBounds = pick(shape, ['x', 'y', 'width', 'height']);
  context.oldParent = oldParent;
  context.oldParentIndex = collectionRemove(oldParent.children, shape);
  collectionAdd(newParent.children, shape, newParentIndex);
  assign(shape, {
    parent: newParent,
    x: shape.x + delta.x,
    y: shape.y + delta.y,
  });
  return shape;
};

SafeMoveShapeHandler.prototype.revert = function (context: any) {
  const shape = context.shape;
  const oldParent = context.oldParent;
  const oldParentIndex = context.oldParentIndex;
  const delta = context.delta;
  if (!oldParent || !oldParent.children) return shape;
  const newParent = shape.parent;
  if (newParent?.children) collectionRemove(newParent.children, shape);
  collectionAdd(oldParent.children, shape, oldParentIndex);
  assign(shape, { parent: oldParent, x: shape.x - delta.x, y: shape.y - delta.y });
  return shape;
};

export default SafeMoveShapeHandler;
